import { useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, LoadingSkeleton, Button, Drawer } from "@narmi/design_system";
import { useSetChatVisibility } from "cerulean";
import { showCancelOption } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import {
  useIsDomesticWireDualApprovalRequired,
  useIsInternationalWireDualApprovalRequired,
  useIsACHDualApprovalRequired,
  useUser,
} from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { getReverseRoute } from "../routes";
import { DrawerHeader } from "../common";
import getPaymentTypeDrawerConfig from "./paymentTypeDrawerConfig";
import usePaymentDrawerData from "./usePaymentDrawerData";
import TopCard from "./cards/TopCard";
import PaymentDetailsCard from "./cards/PaymentDetailsCard";
import RecipientDetailsCard from "./cards/RecipientDetailsCard";

export type PaymentMetadataProps = {
  entry_desc?: API.ACHPayment["entry_desc"];
  std_ent_cls_code?: API.ACHPayment["std_ent_cls_code"];
  description?: API.ACHPayment["description"];
  memo?: API.ACHPayment["memo"] | API.SwiftTransaction["memo"];
  note?: API.SwiftTransaction["note"];
  company_name?: API.ACHPayment["company_name"];
  company_id?: API.ACHPayment["company_id"];
  payment_identification?:
    | API.FedNowPayment["payment_identification"]
    | API.SwiftTransaction["payment_identification"];
  wire_reason?: API.Wire["wire_reason"];
  memo_1?: API.Wire["memo_1"];
  memo_2?: API.Wire["memo_2"];
  memo_3?: API.Wire["memo_3"];
  memo_4?: API.Wire["memo_4"];
  beneficiary_address_1?: API.Wire["beneficiary_address_1"];
  beneficiary_address_2?: API.Wire["beneficiary_address_2"];
  beneficiary_address_3?: API.Wire["beneficiary_address_3"];
  template_name?: API.SwiftTransaction["template_name"];
  quote?: API.FetchExchangeRateQuote.Response;
};

export type PaymentActivityDrawerProps = {
  isOpen: boolean;
  payment: API.FormattedPaymentActivity | null;
  handleClose: () => void;
  handleToggleCancelDialog?: (target: {
    id: string;
    model: string;
    usesDualApproval: boolean;
  }) => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  paymentUuid: API.ScheduledACHPaymentId | API.ACHPaymentId;
  /** Hide all footer actions, e.g. on the reversal selection screen */
  hideFooterActions?: boolean;
};

const PaymentActivityDrawer = ({
  isOpen,
  payment,
  handleClose,
  handleToggleCancelDialog,
  handleOnNext,
  handleOnPrev,
  paymentUuid,
  hideFooterActions = false,
}: PaymentActivityDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`,
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    paymentActivity,
    paymentMetadata,
    institutionDetails,
    renameFields,
    correspondentBankName,
    bulkTitle,
    wireImportBatchTitle,
    loading,
  } = usePaymentDrawerData({ payment, paymentUuid });

  const isDomesticWireDualApprovalRequired =
    useIsDomesticWireDualApprovalRequired();
  const isInternationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const isAchReversalDualApprovalRequired = useIsACHDualApprovalRequired();
  const currentUser = useUser();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const institution = useInstitutionSettings();

  const drawerConfig = useMemo(
    () =>
      getPaymentTypeDrawerConfig({
        l10n,
        institutionDetails,
        renameFields,
        correspondentBankName,
      }),
    [l10n, institutionDetails, renameFields, correspondentBankName],
  );

  if (!paymentActivity) return null;

  const { uuid, isCollectPayment, isReversal } = paymentActivity;

  const showCancelButton =
    !hideFooterActions &&
    showCancelOption(paymentActivity, {
      currentUserUuid: currentUser?.id,
      isDomesticWireDualApprovalRequired,
      isInternationalWireDualApprovalRequired,
      isAchReversalDualApprovalRequired,
    });
  const hasReversePermission = isCollectPayment
    ? hasReverseCollectPermission
    : hasReverseSendPermission;
  const showReverseButton =
    !hideFooterActions &&
    institution?.features?.user_initiated_ach_reversals &&
    paymentActivity.isReversible &&
    hasReversePermission;

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      {...((showCancelButton || showReverseButton) && {
        footer: (
          <Row justifyContent="end">
            {showReverseButton && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() =>
                    navigate(`${getReverseRoute(uuid)}${location.search}`)
                  }
                  label={l10n.getString("label-reverse-payment")}
                />
              </Row.Item>
            )}
            {showCancelButton && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() => {
                    const model = paymentActivity?.model;
                    const isWire =
                      model === "WireTransaction" ||
                      model === "SwiftWireTransaction";
                    // wires and ACH reversals are cancelled via their dual
                    // approval, so pass the dual approval id, not the uuid
                    const usesDualApproval =
                      (isWire || isReversal) &&
                      Boolean(paymentActivity?.dualApprovalId);
                    handleToggleCancelDialog?.({
                      id: usesDualApproval
                        ? (paymentActivity?.dualApprovalId as string)
                        : (paymentActivity?.uuid as string),
                      model: model as string,
                      usesDualApproval,
                    });
                  }}
                  label={l10n.getString("label-cancel-payment")}
                />
              </Row.Item>
            )}
          </Row>
        ),
      })}
    >
      <LoadingSkeleton content="paragraph" showTitle isLoading={loading}>
        <DrawerHeader
          title={l10n.getString("payment-activity-drawer-title")}
          status={paymentActivity.displayStatus}
        />
        <TopCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          bulkTitle={bulkTitle}
          wireImportBatchTitle={wireImportBatchTitle}
          hasDualApprovalPermission={hasDualApprovalPermission}
        />
        <PaymentDetailsCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          drawerConfig={drawerConfig}
        />
        <RecipientDetailsCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          drawerConfig={drawerConfig}
        />
      </LoadingSkeleton>
    </Drawer>
  );
};

export default PaymentActivityDrawer;
